/**
 * DocumentStateChangeDetector - Utility for detecting document state changes
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for detecting when document state (hash, text, etc.)
 * has changed and determining what cache invalidation actions are needed.
 */

/**
 * Result of document state change detection
 */
export interface DocumentStateChangeResult {
  hasChanged: boolean;
  previousHash: string | null;
  newHash: string;
  requiresCacheInvalidation: boolean;
}

/**
 * Detect if document hash has changed
 * 
 * Compares the new document hash with the previous hash and determines
 * if cache invalidation is needed.
 * 
 * @param newHash - New document hash
 * @param previousHash - Previous document hash (can be null/undefined)
 * @returns Object indicating if document changed and requires cache invalidation
 */
export function detectDocumentHashChange(
  newHash: string,
  previousHash: string | null | undefined
): DocumentStateChangeResult {
  const hasChanged = previousHash !== null && previousHash !== undefined && newHash !== previousHash;
  
  return {
    hasChanged,
    previousHash: previousHash || null,
    newHash,
    requiresCacheInvalidation: hasChanged
  };
}

/**
 * Check if document text length has changed significantly
 * 
 * Determines if the text length change is significant enough to warrant
 * cache invalidation or other actions.
 * 
 * @param oldLength - Previous text length
 * @param newLength - New text length
 * @param threshold - Percentage change threshold (default: 0.1 = 10%)
 * @returns True if change is significant
 */
export function isTextLengthChangeSignificant(
  oldLength: number,
  newLength: number,
  threshold: number = 0.1
): boolean {
  if (oldLength === 0) {
    return newLength > 0; // Any text added to empty document is significant
  }
  
  const changeRatio = Math.abs(newLength - oldLength) / oldLength;
  return changeRatio >= threshold;
}

/**
 * Determine cache invalidation strategy based on document change
 * 
 * @param changeResult - Document state change detection result
 * @param options - Additional options for invalidation strategy
 * @returns Object with invalidation recommendations
 */
export function determineInvalidationStrategy(
  changeResult: DocumentStateChangeResult,
  options: {
    clearAllCache?: boolean;
    clearFromPage?: number;
  } = {}
): {
  shouldClearCache: boolean;
  shouldClearAll: boolean;
  clearFromPage: number | null;
} {
  if (!changeResult.requiresCacheInvalidation) {
    return {
      shouldClearCache: false,
      shouldClearAll: false,
      clearFromPage: null
    };
  }
  
  return {
    shouldClearCache: true,
    shouldClearAll: options.clearAllCache ?? true,
    clearFromPage: options.clearFromPage ?? null
  };
}

